/**
 * Region URL rules. optra-labs.com is one domain with one site per region:
 *
 *   /            region chooser (no region)
 *   /blog        region-neutral blog index: every region's posts (a post is read at /<region>/blog/<id>)
 *   /sg/...      OPTRA Singapore: this SPA, mounted with <Router basename="/sg">
 *   /us/...      OPTRA US marketing pages: this SPA, mounted with <Router basename="/us">
 *   /us/app/...  OPTRA US product: a separate Next.js app behind a Vercel rewrite (never this SPA)
 *
 * Inside a region, React Router paths stay region-relative ("/dashboard", "/blog/x") because the
 * basename adds the prefix. Anything that leaves React Router needs a full path from the helpers
 * below: plain <a href>, window.location, Clerk redirect URLs, canonical / Open Graph URLs, and
 * every link from one region to another (those must be plain <a href>, never <Link>, because each
 * region is its own router).
 */

export const SITE_ORIGIN = "https://www.optra-labs.com";

export const REGIONS = {
  sg: { code: "sg", base: "/sg", country: "SG", name: "Singapore" },
  us: { code: "us", base: "/us", country: "US", name: "United States" },
};

export const DEFAULT_REGION = "sg";
const US_APP_DEFAULT = "/us/app";

/** "sg" | "us" | null for a URL pathname. "/sg", "/sg/x" -> "sg"; "/sgx", "/" -> null. */
export function getRegionFromPath(pathname) {
  const p = String(pathname || "").toLowerCase();
  for (const region of Object.values(REGIONS)) {
    if (p === region.base || p.startsWith(`${region.base}/`)) return region.code;
  }
  return null;
}

/** Region of the page being shown, from the address bar. null on the chooser and during SSR/tests without a URL. */
export function getRegion() {
  if (typeof window === "undefined") return null;
  return getRegionFromPath(window.location.pathname);
}

/** True for URLs owned by the US Next.js product rather than this SPA. */
export function isUsAppPath(pathname) {
  const p = String(pathname || "").toLowerCase();
  return p === US_APP_DEFAULT || p.startsWith(`${US_APP_DEFAULT}/`);
}

function normalizePath(path) {
  const raw = String(path ?? "/");
  if (raw === "" || raw === "/") return "";
  return raw.startsWith("/") ? raw : `/${raw}`;
}

/** Full same-origin path into a region: regionHref("sg", "/login") -> "/sg/login"; regionHref("us") -> "/us". */
export function regionHref(region, path = "/") {
  const r = REGIONS[region] || REGIONS[DEFAULT_REGION];
  return `${r.base}${normalizePath(path)}`;
}

/**
 * Base of the US product. "/us/app" in production (Vercel rewrite) and in local dev (Vite proxy);
 * VITE_US_APP_URL overrides it, e.g. "http://localhost:3000/us/app" to bypass the proxy.
 */
export function usAppBase() {
  let raw = "";
  try {
    raw = String(import.meta.env?.VITE_US_APP_URL || "").trim();
  } catch {
    raw = "";
  }
  const base = /^(https?:\/\/|\/)/.test(raw) ? raw : US_APP_DEFAULT;
  return base.replace(/\/+$/, "");
}

/** Link into the US product: usAppHref("/login") -> "/us/app/login". Always a plain <a href>. */
export function usAppHref(path = "/") {
  return `${usAppBase()}${normalizePath(path)}`;
}

/** Absolute canonical URL: canonicalUrl("/about", "sg") -> "https://www.optra-labs.com/sg/about". Region defaults to the current one, then Singapore. */
export function canonicalUrl(path = "/", region = getRegion() || DEFAULT_REGION) {
  return `${SITE_ORIGIN}${regionHref(region, path)}`;
}

/** Region-neutral pages of this SPA, outside both regional routers. Trailing slashes are ignored. */
const GLOBAL_PATHS = ["/", "/blog", "/about"];

export function isGlobalPath(pathname) {
  const p = String(pathname || "/").toLowerCase().replace(/\/+$/, "") || "/";
  return GLOBAL_PATHS.includes(p);
}

/**
 * Where a pre-region URL now lives: "/dashboard?x=1#y" -> "/sg/dashboard?x=1#y". Every URL that
 * existed before regions was a Singapore URL, so emailed links and bookmarks keep working.
 * Returns null when the path needs no redirect (a region-neutral page, a region, or the US product).
 */
export function legacyRedirectTarget(pathname, search = "", hash = "") {
  const p = String(pathname || "/");
  // "/blog" itself is the region-neutral index now; "/blog/<post>" is still a pre-region Singapore URL.
  if (isGlobalPath(p) || getRegionFromPath(p) || isUsAppPath(p)) return null;
  return `${REGIONS.sg.base}${p}${search || ""}${hash || ""}`;
}
